import { useCallback, useContext, useState } from 'react';
import { FormApiInterface, Validator } from 'react-uforms';
import axios from 'axios';
import { AppContext } from '../../app-context';
import { Endpoints, fileTypes, REACT_APP_ENV_URL, supportedFileExtensions } from '../../const';
import { QuizContext } from '../quiz-context';
import { arrayToOptions } from '@ch/common/utils/helpers';
import { useData } from '../../use-data';
import { Document, FileOptionsSelect, fileOptionsSelectArray, FileOptions } from '../types';
import { getFileExtension } from '../helpers/file';
import { DocumentsMeta, questionSpecialIdentifiers } from '@ch/common/types/data';

export const useQuizDocument = (id: string, documentsMeta: DocumentsMeta, api: FormApiInterface) => {
  const {
    documents: contextDocuments,
    setDocuments,
    addNotification,
    intakeId,
    token,
    setValidation,
    validation,
  } = useContext(QuizContext);
  const { setLoading } = useContext(AppContext);
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);
  const {
    data: {
      keywords: {
        docClassification,
        docDescription,
        fileReports,
        uploaded,
        uploadMaxCount = 10,
        uploadMaxSize = 15 * 1024 * 1024,
      },
      errors: {
        errorKey,
        uploadSizeLimit = 'Uploaded file is too big',
        uploadSupportedFormats = 'Formats supported are PDF, XLS, XLSX, DOC, DOCX, TXT, CSV, TSV, JPG, JPEG, PNG and MSG',
        uploadCountLimit = 'Maximum files to upload limit reached',
      },
      mandatory: { mandatory_message: requiredMessage },
    },
  } = useData();

  const { classification, description } = documentsMeta ?? {};
  const documents: Document[] = contextDocuments[id] ?? [];

  const currentPersona = api.getValue(questionSpecialIdentifiers.PERSON_TYPE);
  const currentFlow = api.getValue(questionSpecialIdentifiers.FLOW);

  const isFileTypeVisible =
    classification?.personas?.includes(currentPersona) && classification?.events?.includes(currentFlow);

  const isFileReportVisible =
    description?.personas?.includes(currentPersona) && description?.events?.includes(currentFlow);

  const fileTypesOptions = arrayToOptions(fileTypes);
  const fileReportsOptions = arrayToOptions(fileReports.map(r => r?.value ?? r));

  const fieldMapping: FileOptions = {
    ...(isFileTypeVisible && {
      [`${id}_${FileOptionsSelect.FILE_TYPE}`]: {
        title: docClassification,
        options: fileTypesOptions,
      },
    }),
    ...(isFileReportVisible && {
      [`${id}_${FileOptionsSelect.FILE_REPORT}`]: {
        title: docDescription,
        options: fileReportsOptions,
        descriptions: fileReports,
      },
    }),
  };
  const config = {
    headers: {
      token,
    },
  };

  const uploadDocuments = useCallback(
    async (files: File[], values: Record<string, unknown>) => {
      return Promise.all(
        files.map(async file => {
          const { name, size, type } = file;
          const defaultParams = {
            fileName: name,
            intakeId,
          };
          const params = new URLSearchParams({
            ...defaultParams,
          }).toString();

          const {
            data: { url },
          } = await axios.post(`${REACT_APP_ENV_URL}${Endpoints.GET_SIGN_URL}?${params}`, {}, config);
          await axios.put(url, file, config);
          const uploadParams = new URLSearchParams({
            ...defaultParams,
            fileDetails: JSON.stringify([
              {
                fileName: name,
                fileSize: size,
                fileType: type,
              },
            ]),
          }).toString();
          await axios.post(`${REACT_APP_ENV_URL}${Endpoints.UPLOAD_FILE}?${uploadParams}`, {}, config);
          return {
            url,
            file,
            values: Object.entries(values).reduce(
              (result, [key, value]) => ({
                ...result,
                ...(fileOptionsSelectArray.map(option => `${id}_${option}`).includes(key) && { [key]: value }),
              }),
              {},
            ),
          } as Document;
        }),
      );
    },
    [id, intakeId, config],
  );

  const isValidFileExtension = useCallback((file: File) => {
    return supportedFileExtensions.includes(getFileExtension(file.name));
  }, []);

  const isValidFileSize = useCallback((file: File) => file.size < +uploadMaxSize, [uploadMaxSize]);

  const isValidFilesCount = useCallback(() => documents.length + currentFiles.length <= +uploadMaxCount, [
    documents,
    currentFiles,
    uploadMaxCount,
  ]);

  const isInputValid = useCallback(
    (api: FormApiInterface) => {
      const values = api.getAllValues();
      const fileTypeId = `${id}_${FileOptionsSelect.FILE_TYPE}`;
      const fileReportId = `${id}_${FileOptionsSelect.FILE_REPORT}`;
      const isFileTypeValid = isFileTypeVisible && !values[fileTypeId];
      const isFileReportValid = isFileReportVisible && !values[fileReportId];

      if (isFileTypeValid || isFileReportValid) {
        const documentsToQuestion = {
          ...(isFileTypeValid && { [fileTypeId]: [Validator.Required(requiredMessage)] }),
          ...(isFileReportValid && { [fileReportId]: [Validator.Required(requiredMessage)] }),
        };

        setValidation({ ...validation, ...documentsToQuestion });

        //HACK: without this delay the validation won't work for the first time
        setTimeout(() => {
          [fileTypeId, fileReportId].forEach(option => api.setTouched(option));
        }, 200);

        return false;
      }

      return true;
    },
    [id, validation, setValidation, requiredMessage, isFileTypeVisible, isFileReportVisible],
  );

  const handleUpload = useCallback(
    async api => {
      const values = api.getAllValues();

      if (!isInputValid(api)) {
        return;
      }

      setLoading(true);
      try {
        if (!isValidFilesCount()) {
          addNotification({ title: uploadCountLimit, type: 'error' });
          return;
        }

        const files = Object.entries(currentFiles).map(([, value]) => value);
        if (!files.every(isValidFileSize)) {
          addNotification({ title: uploadSizeLimit, type: 'error' });
          return;
        }
        if (!files.every(isValidFileExtension)) {
          addNotification({ title: uploadSupportedFormats, type: 'error' });
          return;
        }
        const uploadedFiles = await uploadDocuments(files, values);

        setDocuments({
          ...contextDocuments,
          [id]: [...documents, ...uploadedFiles],
        });

        for (const field of Object.keys(fieldMapping)) {
          api.setValue(field, '');
        }

        setCurrentFiles([]);
        api.setValue(`${id}_required`, 'processed');
        addNotification({ title: uploaded, type: 'success' });
      } catch (error) {
        addNotification({ title: errorKey, type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [
      id,
      contextDocuments,
      documents,
      currentFiles,
      fieldMapping,
      setDocuments,
      setLoading,
      errorKey,
      uploaded,
      addNotification,
      uploadDocuments,
      isValidFileSize,
      isValidFileExtension,
      isValidFilesCount,
      isInputValid,
      uploadMaxSize,
      uploadSizeLimit,
      uploadCountLimit,
      uploadSupportedFormats,
    ],
  );

  const handleChange = useCallback(
    e => {
      setCurrentFiles(e.target.files);
    },
    [setCurrentFiles],
  );

  const deleteFromApi = useCallback(
    async ({ name, size, type }: File) => {
      const deleteParams = new URLSearchParams({
        fileName: name,
        intakeId,
        fileDetails: JSON.stringify([
          {
            fileName: name,
            fileSize: size,
            fileType: type,
          },
        ]),
      }).toString();
      setLoading(true);
      await axios.delete(`${REACT_APP_ENV_URL}${Endpoints.DELETE_FILE}?${deleteParams}`, config);
      setLoading(false);
    },
    [setLoading, config, intakeId],
  );

  const handleDelete = useCallback(
    async (url, api) => {
      const { file } = documents.find(({ url: docUrl }) => url === docUrl);
      await deleteFromApi(file);
      const filtered = documents.filter(({ url: docUrl }) => url !== docUrl);
      setDocuments({
        ...contextDocuments,
        [id]: filtered,
      });

      if (documents.length <= 1) {
        api.setValue(`${id}_required`, '');
      }
    },
    [id, documents, contextDocuments, setDocuments, deleteFromApi],
  );

  return {
    uploadDocuments,
    documents,
    fieldMapping,
    handleChange,
    handleUpload,
    handleDelete,
    isValidFileSize,
    deleteFromApi,
  };
};
